import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      // Cookie-based session works too via `withCredentials`
      const r = await api.get("/auth/me", { withCredentials: true });
      setUser(r.data.user);
    } catch {
      const t = localStorage.getItem("pba_token");
      if (t) localStorage.removeItem("pba_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("pba_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, display_name) => {
    const r = await api.post("/auth/register", { email, password, display_name });
    localStorage.setItem("pba_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (session_id) => {
    const r = await api.post("/auth/google/session", { session_id }, { withCredentials: true });
    // Also stash the token in localStorage as a Bearer fallback for API calls
    if (r.data.token) localStorage.setItem("pba_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch { /* noop */ }
    localStorage.removeItem("pba_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, loginWithGoogle, exchangeGoogleSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
